import { type EditorPlugin, type LinkElement } from '../../types/aha-editor';
import LinkComponent from './components/LinkComponent';
import tools from '../../tools';
import { Transforms, Editor, Range, Text } from 'slate';
import type AhaEditor from '../../AhaEditor';
import isUrl from 'is-url';
import { wrapLink, detectLink } from './utils';
import LinkNormalizationPlugin from './LinkNormalization';
import { isLinkElement } from '../../types/guards';
import { isInlineActive, unwrapInline } from '../InlinePlugin/utils';
import LinkSchemaPlugin from './LinkSchemaPlugin';

export const LinkComponentPlugin: EditorPlugin = {
  name: 'Link component plugin',
  components: [
    {
      type: 'link',
      component: LinkComponent,
      toolbarItems: [
        [tools.ToolOpenLink],
        [tools.ToolEditLink],
        [tools.ToolUnlink],
      ],
    },
  ],
};

// When pasting something that looks like a URL, automatically convert it
// to a link.
const LinkPastePlugin: EditorPlugin = {
  name: 'Link Paste Plugin',
  dependencies: [LinkComponentPlugin],
  insertData: (editor: Editor, data: DataTransfer) => {
    const text = data.getData('text/plain')?.trim();

    if (text && isUrl(text)) {
      if (isInlineActive(editor, 'link')) {
        unwrapInline(editor, 'link');
      }

      wrapLink(editor, text, text);
      Transforms.move(editor, { unit: 'offset', distance: 1 });

      return true;
    } else {
      return false;
    }
  },
};

// TODO: Move this to Utils
const convertLinks = (
  event: KeyboardEvent,
  ahaEditor: AhaEditor
): boolean | undefined => {
  const { editor } = ahaEditor;

  if (event.key !== ' ' && event.key !== 'Enter') return;

  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return;

  const link = Editor.node(editor, selection)[0];
  if (isLinkElement(link)) return;

  // Get the preceding text.
  const nodeEntry = Editor.node(editor, selection.anchor.path);
  if (!nodeEntry) return;

  const [node] = nodeEntry;
  if (!Text.isText(node)) return;
  // Extract the text before the cursor.
  const lastText = node.text.slice(0, selection.focus.offset);
  const match = detectLink(lastText);
  if (!match) return;

  const { url, offset } = match;
  const newNode: LinkElement = {
    type: 'link',
    link_href: url,
    children: [{ text: url }],
  };

  Transforms.wrapNodes(editor, newNode, {
    at: {
      anchor: { path: selection.focus.path, offset },
      focus: { path: selection.focus.path, offset: selection.focus.offset },
    },
    split: true,
  });

  // When text is at end block then move it to next block.
  if (Editor.isEnd(editor, selection.focus, selection.focus)) {
    Transforms.move(editor, { unit: 'offset', distance: 1 });
  }

  if (event.key === ' ') {
    editor.insertText(' ');
  } else if (event.key === 'Enter') {
    Transforms.splitNodes(editor, { always: true });
  }

  event.preventDefault();
  return true;
};

// Detect hyperlinks and convert to a link node.
export const LinkDetectPlugin: EditorPlugin = {
  name: 'Link detect plugin',
  dependencies: [LinkComponentPlugin],
  handlers: {
    onKeyDown: convertLinks,
  },
};

const LinkPlugin: EditorPlugin[] = [
  LinkSchemaPlugin,
  LinkPastePlugin,
  LinkDetectPlugin,
  LinkComponentPlugin,
  LinkNormalizationPlugin,
];

export default LinkPlugin;
